// src/pages/Dashboard/sections/network-services/networkDataUtils.js

/**
 * Helper function to find the superior node ID for an indirect subordinate
 * @param {Object} node - The node to find the superior for
 * @param {Array} directSubordinates - List of direct subordinates
 * @param {string} rootNodeId - ID of the root node
 * @returns {string} - ID of the superior node
 */
export const findSuperiorNodeId = (node, directSubordinates, rootNodeId) => {
  // If the API provides this information directly, use it
  if (node.superior_node_id) {
    return node.superior_node_id;
  }
  
  // Try to find based on parent_id if available
  if (node.parent_id) {
    const parent = directSubordinates.find(d => d.id === node.parent_id);
    if (parent) {
      return parent.id;
    }
  }
  
  // As a fallback, try to determine based on node's hierarchical reference code if available
  if (node.reference_code && directSubordinates.length > 0) {
    for (const directNode of directSubordinates) {
      if (directNode.reference_code && node.reference_code.startsWith(directNode.reference_code)) {
        return directNode.id;
      }
    }
  }
  
  // If no relationship can be determined, fallback to connecting to the root
  return rootNodeId;
};

/**
 * Generate network graph data from API response
 * @param {Object} networkData - API response data
 * @param {Object} rootNode - The current selected node
 * @param {Function} formatWalletAddress - Function to format wallet addresses
 * @param {Object} feesData - Optional network stability fees data
 * @returns {Object} - Graph data in format { nodes: [], links: [] }
 */
export const generateNetworkGraph = (networkData, rootNode, formatWalletAddress, feesData = null) => {
  // Default graph with just the root node if no network data
  if (!networkData || !rootNode) {
    return {
      nodes: [
        {
          id: rootNode.id,
          name: rootNode.reference_code || 'Root Node',
          tier: rootNode.tier || 'Standard',
          wallet: rootNode.wallet_address,
          group: 0,
          level: 0,
          isRoot: true,
          // Add stability fees data to root node if available
          stability_fees: feesData && feesData.summary ? {
            total_amount: feesData.summary.total_fees_received,
            self_referral_amount: feesData.summary.self_referral_amount,
            external_fees_amount: feesData.summary.external_fees_amount,
            last_updated: feesData.summary.last_updated
          } : undefined
        }
      ],
      links: []
    };
  }
  
  // Extract direct and indirect subordinates from the response
  const allSubordinates = networkData.subordinates || [];
  const directSubordinates = allSubordinates.filter(node => node.relationship_level === 1);
  const indirectSubordinates = allSubordinates.filter(node => node.relationship_level === 2);
  
  // Process fees data to associate with nodes
  const nodesFeeData = {};
  if (feesData && feesData.fees) {
    feesData.fees.forEach(fee => {
      // If the fee has source node info
      if (fee.subordinate_node) {
        const nodeId = fee.subordinate_node.reference_code;
        if (!nodesFeeData[nodeId]) {
          nodesFeeData[nodeId] = {
            total_amount: 0,
            transactions: [],
            source_node: fee.subordinate_node,
            is_self_referral: fee.is_self_referral
          };
        }
        
        // Add fee amount to total
        nodesFeeData[nodeId].total_amount += fee.amount;
        
        // Record transaction info
        nodesFeeData[nodeId].transactions.push({
          amount: fee.amount,
          created_at: fee.created_at,
          transaction_id: fee.transaction_id
        });
        
        // Update last updated time if this is more recent
        if (!nodesFeeData[nodeId].last_updated || 
            new Date(fee.created_at) > new Date(nodesFeeData[nodeId].last_updated)) {
          nodesFeeData[nodeId].last_updated = fee.created_at;
        }
      }
    });
  }
  
  // Build nodes array
  const nodes = [
    // Root node (selected node)
    {
      id: rootNode.id,
      name: networkData.node?.reference_code || rootNode.reference_code || 'Your Node',
      tier: networkData.node?.tier || rootNode.tier_name || 'Standard',
      wallet: networkData.node?.wallet_address || rootNode.wallet_address,
      group: 0,
      level: 0,
      isRoot: true,
      // Add stability fees data to root node if available
      stability_fees: feesData && feesData.summary ? {
        total_amount: feesData.summary.total_fees_received,
        self_referral_amount: feesData.summary.self_referral_amount,
        external_fees_amount: feesData.summary.external_fees_amount,
        last_updated: feesData.summary.last_updated
      } : undefined
    },
    // Direct subordinates (level 1)
    ...directSubordinates.map(node => {
      const nodeId = node.reference_code || node.id;
      const feeData = nodesFeeData[nodeId];
      
      return {
        id: node.id,
        name: node.reference_code || formatWalletAddress(node.wallet_address),
        tier: node.tier_name || 'Standard',
        wallet: node.wallet_address,
        group: 1,
        level: 1,
        directSubordinate: true,
        superior_node_id: rootNode.id, // Set parent node ID to root node
        stability_fees: feeData ? {
          total_amount: feeData.total_amount,
          last_updated: feeData.last_updated,
          transactions: feeData.transactions,
          source_node: feeData.source_node,
          is_self_referral: feeData.is_self_referral
        } : undefined
      };
    }),
    // Indirect subordinates (level 2)
    ...indirectSubordinates.map(node => {
      const nodeId = node.reference_code || node.id;
      const feeData = nodesFeeData[nodeId];
      
      return {
        id: node.id,
        name: node.reference_code || formatWalletAddress(node.wallet_address),
        tier: node.tier_name || 'Standard',
        wallet: node.wallet_address,
        group: 2,
        level: 2,
        indirectSubordinate: true,
        // Find the parent node ID
        superior_node_id: findSuperiorNodeId(node, directSubordinates, rootNode.id),
        stability_fees: feeData ? {
          total_amount: feeData.total_amount,
          last_updated: feeData.last_updated,
          transactions: feeData.transactions,
          source_node: feeData.source_node,
          is_self_referral: feeData.is_self_referral
        } : undefined
      };
    })
  ];
  
  // Build links array
  const links = [
    // Links from root to direct subordinates
    ...directSubordinates.map(node => ({
      source: rootNode.id,
      target: node.id,
      value: 1
    })),
    // Links from direct to indirect subordinates
    ...indirectSubordinates.map(node => {
      const superiorId = findSuperiorNodeId(node, directSubordinates, rootNode.id);
      return {
        source: superiorId, 
        target: node.id,
        value: 1
      };
    })
  ];
  
  return { nodes, links };
};
